import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {toastr} from "react-redux-toastr";
import { IUserPayload, IRole, IUserRolesState, ICMSUser } from "./RolesList.types";
import { API_ROLES, API_USERS, API_USERS_INIT } from "../../../services/api/api.urls";
import { api } from "../../../services/api/api.service";
import { IdName } from "../../../common/types/IdName";
import { TryCatch } from "../../../services/TryCatch";
import { COMPANY_ABBR } from "../../../common/constants/company";
import IUser from "../../../common/interfaces/IUser";

interface IGetUserResponse {
    totalItems: number,
    totalPages: number,
    pageNum: number,
    pageSize: number,
    items: ICMSUser[]
}

interface IGetRolesResponse {
    totalItems: number,
    totalPages: number,
    pageNum: number,
    pageSize: number,
    items: IRole[]
}

export interface IGetAllUserClients {
    clients: IdName[]
    allowCRUD: boolean
}

const initialState: IUserRolesState = {
    roles: [] as IRole[],
    allUsers: [] as ICMSUser[],
    user: {} as ICMSUser,
    usersLoader: true,
    rolesLoader: true,
    createMode: false,
    editMode: false,
    clientId: null,
    allClients: [],
    filterValues: {
        from: '',
        search: '',
        status: 0,
        pNum: 1,
        pSize: 10,
        sort: false,
    },
    totalItems: 15,
    totalPages: 1,
    pageNum: 1,
    pageSize:10,
    allowCRUD: false
}


export const getRoles = createAsyncThunk<IGetRolesResponse, void, { rejectValue: string }>(
    'roles/get',
    async (_, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(API_ROLES).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch roles.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getUsers = createAsyncThunk<IGetUserResponse, string, { rejectValue: string }>(
    'users/get',
    async (params, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_USERS}/${params}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch users.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getAllClients = createAsyncThunk<IGetAllUserClients, void, { rejectValue: string }>(
    'users/get-all-clients',
    async (_, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_USERS_INIT}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch clients.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const getSingleUser = createAsyncThunk<ICMSUser, number, { rejectValue: string }>(
    'users/getSingle',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_USERS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch user.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const createUser = createAsyncThunk<ICMSUser, IUserPayload, { rejectValue: string }>(
    'users/post',
    async (user, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.post(API_USERS, user).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to create user.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "User has been created");
        return result.data;
    }
)

export const editUser = createAsyncThunk<ICMSUser, IUserPayload, { rejectValue: string }>(
    'users/edit',
    async (user, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_USERS}`, user).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to edit user.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "User has been edited");
        return result.data;
    }
)

export const deleteUser = createAsyncThunk<IUser, number, { rejectValue: string }>(
    'users/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_USERS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to delete user.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "User has been deleted.");
        return result.data;
    }
)

export const RolesList = createSlice({name: 'RolesList',
    reducers: {
        createModeHandler: (state, action: PayloadAction<boolean>) => {
            state.createMode = action.payload;
        },
        editModeHandler: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload;
        },
        clearSingleUserState: (state) => {
            state.user = initialState.user;
        },
        setActivePage: (state, action) => {
            state.pageNum = action.payload
        },
        changePageSize: (state, action) => {
            state.pageSize = action.payload
        },
        setClientId: (state, action) => {
            state.clientId = action.payload
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getRoles.fulfilled, (state: IUserRolesState, action) => {
                state.rolesLoader = false;
                state.roles = action.payload.items;
            })
            .addCase(getRoles.rejected, (state: IUserRolesState) => {
                state.rolesLoader = false;
            })
            .addCase(getUsers.fulfilled, (state: IUserRolesState, action) => {
                state.usersLoader = false;
                state.allUsers = action.payload.items;
                state.totalPages = action.payload.totalPages;
                if (action.payload.pageNum === 0) {
                    state.pageNum = 1
                } else {
                    state.pageNum = action.payload.pageNum
                }
            })
            .addCase(getUsers.rejected, (state: IUserRolesState) => {
                state.usersLoader = false;
            })
            .addCase(getAllClients.fulfilled, (state, action) => {
                state.allClients = action.payload.clients
                state.allowCRUD = action.payload.allowCRUD

            })
            .addCase(getSingleUser.fulfilled, (state: IUserRolesState, action) => {
                state.editMode = true;
                state.user = action.payload;
            })
            .addCase(createUser.pending, (state: IUserRolesState) => {
                state.createMode = false;
                state.usersLoader = true;
            })
            .addCase(createUser.fulfilled, (state: IUserRolesState, action) => {
                state.allUsers = state.allUsers.concat(action.payload);
                state.usersLoader = false;
                state.pageNum = 1
            })
            .addCase(createUser.rejected, (state: IUserRolesState) => {
                state.usersLoader = false
            })
            .addCase(editUser.pending, (state: IUserRolesState) => {
                state.editMode = false;
                state.usersLoader = true;
            })
            .addCase(editUser.fulfilled, (state: IUserRolesState, action) => {
                state.usersLoader = false;
                state.user = {} as ICMSUser;
                //state.allUsers = state.allUsers.map((user) => {
                //    if (user.id === action.payload.id) {
                //        user = action.payload
                //    }
                //    return user
                //})

            })
            .addCase(editUser.rejected, (state: IUserRolesState) => {
                state.usersLoader = false;
            })
            .addCase(deleteUser.pending, (state: IUserRolesState) => {
                state.usersLoader = true;
            })
            .addCase(deleteUser.fulfilled, (state: IUserRolesState, action) => {
                //state.allUsers = state.allUsers.filter((user) => {
                //    return user.id !== action.meta.arg
                //})
                state.usersLoader = false;
            })
            .addCase(deleteUser.rejected, (state: IUserRolesState) => {
                state.usersLoader = false;
            })
    },
});
export const {
    createModeHandler,
    editModeHandler,
    clearSingleUserState,
    setActivePage,
    setClientId,
    changePageSize
} = RolesList.actions

export default RolesList.reducer;
